import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@/providers/RouterShim';
import { useCustomerDrawer } from '../../data/entityDrawer/useCustomerDrawer';
import Alert from '../../styled-components/components/Alert';
import { Form, FormGroup } from '../../styled-components/components/Form';
import InputStaticValue from '../../styled-components/components/InputStaticValue';
import { RouterLink } from '../../styled-components/components/Link';
import { ErrorContainer, LoadingContainer } from './styled';

interface CustomerDrawerContentProps {
  entityId: string;
  onClose: () => void;
}

/**
 * Customer drawer content component
 * Displays customer information in the drawer
 */
export const CustomerDrawerContent: React.FC<CustomerDrawerContentProps> = ({
  entityId,
  onClose,
}) => {
  const [customer, { isLoading, isError }] = useCustomerDrawer(entityId);
  const { branchId } = useParams();
  const { t } = useTranslation(['common', 'glossary']);

  if (isLoading) {
    return <LoadingContainer>{t('drawer.loadingCustomerData', { ns: 'common' })}</LoadingContainer>;
  }

  if (isError || !customer) {
    return (
      <ErrorContainer>{t('drawer.couldNotLoadCustomerData', { ns: 'common' })}</ErrorContainer>
    );
  }

  // Get primary contact info
  const primaryContactInfos = customer.contactInfos?.filter((ci) => ci.isPrimary) || [];
  const primaryEmail = primaryContactInfos.find(
    (ci) => ci.contactInfo?.type === 'EMAIL',
  )?.contactInfo;
  const primaryPhone = primaryContactInfos.find(
    (ci) => ci.contactInfo?.type === 'PHONE',
  )?.contactInfo;

  // Get primary address
  const primaryAddress = customer.addresses?.find((a) => a.isPrimary);

  // Format address for display
  const formatAddress = () => {
    if (!primaryAddress) return '';
    const parts = [
      primaryAddress.street,
      `${primaryAddress.postalCode} ${primaryAddress.city}`,
      primaryAddress.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const hasInfo =
    customer.personalNumber ||
    primaryEmail ||
    primaryPhone ||
    primaryAddress ||
    (customer.tags && customer.tags.length > 0);

  return (
    <Form>
      {customer.personalNumber && (
        <FormGroup $width="half">
          <InputStaticValue
            label={t('personalNumber', { ns: 'glossary' })}
            value={customer.personalNumber}
          />
        </FormGroup>
      )}

      {primaryPhone && (
        <FormGroup $width="half">
          <InputStaticValue
            label={t('phoneNumber', { ns: 'glossary' })}
            value={primaryPhone.value}
          />
        </FormGroup>
      )}

      {primaryEmail && (
        <FormGroup $width="half">
          <InputStaticValue
            label={t('emailAddress', { ns: 'glossary' })}
            value={primaryEmail.value}
          />
        </FormGroup>
      )}

      {primaryAddress && (
        <FormGroup>
          <InputStaticValue
            label={t('streetAddress', { ns: 'glossary' })}
            value={formatAddress()}
          />
        </FormGroup>
      )}

      {customer.tags && customer.tags.length > 0 && (
        <FormGroup>
          <InputStaticValue
            label={t('tags', { ns: 'glossary' })}
            value={customer.tags.join(', ')}
          />
        </FormGroup>
      )}

      {!hasInfo && (
        <FormGroup>
          <Alert tone="info" message={t('drawer.noAdditionalInformation', { ns: 'common' })} />
        </FormGroup>
      )}

      <FormGroup>
        <RouterLink to={`/${branchId}/customers/contacts/${entityId}`} onClick={onClose}>
          {t('drawer.openFullView', { ns: 'common' })}
        </RouterLink>
      </FormGroup>
    </Form>
  );
};
